import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { useQuery } from '@tanstack/react-query';
import {
  getAllVaultMatters,
  VaultKeys,
  checkVaultAccess,
  VaultMatterModel,
} from '../../../../../api/vault-api.ts';
import { useState, useMemo, useCallback } from 'react';
import CreateVaultMatterDialog from '../../../../import-export/vault-export/create-vault-matter-dialog.tsx';
import { UserKeys } from '../../../../../api/users-api.ts';
import VaultGmailExportConfigOld from './vault-gmail-export-config-old.tsx';
import VaultCalendarExportConfigOld from './vault-calendar-export-config-old.tsx';
import VaultChatExportConfigOld from './vault-chat-export-config-old.tsx';
import VaultGeminiExportConfigOld from './vault-gemini-export-config-old.tsx';
import VaultLicenseError from './shared/vault-license-error.tsx';
import VaultMatterTableOld from './shared/vault-matter-table-old.tsx';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import { isFeatureEnabled } from '../../../../../utils/feature-flags.ts';
import { DestUser } from '../../../../../../types.ts';

import { ActionErrorEntry } from '../decom-policy-utils.tsx';
import { clearFieldError } from './decom-action.tsx';
import VaultDriveExportConfigOld from './vault-drive-export-config-old.tsx';
import VaultGroupsExportConfigOld from './vault-groups-export-config-old.tsx';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import FormHelperText from '@mui/material/FormHelperText';
import Search from '@mui/icons-material/Search';

interface VaultExportStepProps {
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  day: number;
  idx: number;
  actionType: string;
  control: Control<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
}

export default function VaultExportStepOld({
  watch,
  setValue,
  day,
  idx,
  actionType,
  control,
  errors,
  clearErrors,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
}: VaultExportStepProps) {
  const matterId = watch(`actions.${day}.actions.${idx}.matterId`) || '';
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateMatterOpen, setIsCreateMatterOpen] = useState(false);
  const { features } = useUserSettings();

  const isDecomFeatureEnabled = isFeatureEnabled(
    'decommissioningUiUpdate',
    features
  );

  const createMatterOnDecom =
    watch(`actions.${day}.actions.${idx}.createMatterOnDecom`) || false;
  const matterNameTemplateCustomText =
    watch(`actions.${day}.actions.${idx}.matterNameTemplateCustomText`) || '';
  const matterNameTemplateProfileField =
    watch(`actions.${day}.actions.${idx}.matterNameTemplateProfileField`) || '';
  const matterNameTemplateDescription =
    watch(`actions.${day}.actions.${idx}.matterNameTemplateDescription`) || '';
  const matterRegion =
    watch(`actions.${day}.actions.${idx}.matterRegion`) || 'ANY';

  const {
    data: hasVaultAccess,
    isLoading: isLoadingDeprecation,
    isError: isAccessError,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'vault'],
    queryFn: () => checkVaultAccess(),
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: isFeatureEnabled('vaultDecomAction', features),
  });

  const {
    data,
    isLoading: vaultMattersLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [VaultKeys.VaultMatters],
    queryFn: () => getAllVaultMatters(),
    enabled: !!hasVaultAccess,
  });

  const matters = useMemo(() => {
    const allMatters = data || [];

    return allMatters.filter((matter) => {
      const searchLower = searchQuery.toLowerCase();
      return matter.name.toLowerCase().includes(searchLower);
    });
  }, [data, searchQuery]);

  const selectedMatter = matters.find((m) => m.matterId === matterId);
  const isLoading = vaultMattersLoading || isLoadingDeprecation;

  const customSetValue: UseFormSetValue<DecomPolicyFormValues> = useCallback(
    (name, value, options) => {
      setValue(name, value, options);
      if (name === `actions.${day}.actions.${idx}.matterId` && value === '') {
        setValue(`actions.${day}.actions.${idx}.createMatterOnDecom`, false, {
          shouldDirty: true,
        });
      }
    },
    [day, idx, setValue]
  );

  if (hasVaultAccess === false || isAccessError) {
    return <VaultLicenseError />;
  }

  const effectiveMatter: VaultMatterModel = createMatterOnDecom
    ? {
        matterId: 'auto-create',
        name: `${matterNameTemplateCustomText || 'Departed Employee'} - {${
          matterNameTemplateProfileField || 'User Name'
        }}`,
        description: matterNameTemplateDescription || 'Created automatically',
        state: 'OPEN',
      }
    : selectedMatter || {
        matterId: '',
        name: '',
        description: '',
        state: 'OPEN',
      };

  const showConfig =
    createMatterOnDecom || (matterId && (selectedMatter || isLoading));

  if (showConfig) {
    return (
      <>
        {/* 
          To add a new Vault Export type (e.g., Drive, Chat), create a new configuration 
          component similar to VaultGmailExportConfig below, and add it to this switch statement 
          based on its actionType.
        */}
        {actionType === 'vaultGmailExport' && (
          <VaultGmailExportConfigOld
            matter={effectiveMatter}
            isLoading={isLoading}
            day={day}
            idx={idx}
            watch={watch}
            setValue={customSetValue}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        )}
        {actionType === 'vaultCalendarExport' && (
          <VaultCalendarExportConfigOld
            matter={effectiveMatter}
            isLoading={isLoading}
            day={day}
            idx={idx}
            watch={watch}
            setValue={customSetValue}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        )}
        {actionType === 'vaultChatExport' && (
          <VaultChatExportConfigOld
            matter={effectiveMatter}
            isLoading={isLoading}
            day={day}
            idx={idx}
            watch={watch}
            setValue={customSetValue}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        )}
        {actionType === 'vaultDriveExport' && (
          <VaultDriveExportConfigOld
            matter={effectiveMatter}
            isLoading={isLoading}
            day={day}
            idx={idx}
            watch={watch}
            setValue={customSetValue}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        )}
        {actionType === 'vaultGeminiExport' && (
          <VaultGeminiExportConfigOld
            matter={effectiveMatter}
            isLoading={isLoading}
            day={day}
            idx={idx}
            watch={watch}
            setValue={customSetValue}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        )}
        {actionType === 'vaultGroupsExport' && (
          <VaultGroupsExportConfigOld
            matter={effectiveMatter}
            isLoading={isLoading}
            day={day}
            idx={idx}
            watch={watch}
            setValue={customSetValue}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        )}
      </>
    );
  }

  return (
    <Stack
      sx={{
        px: 3,
        py: 3,
        borderTop: isDecomFeatureEnabled ? 'none' : '1px solid',
        borderColor: isDecomFeatureEnabled ? 'unset' : 'divider',
      }}
      gap={2}
      width={'100%'}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography variant="h6" fontWeight={500}>
          Matters ({matters.length})
        </Typography>
        {errors.actions?.[day]?.actions?.[idx]?.matterId?.message && (
          <FormHelperText error sx={{ fontSize: '0.875rem' }}>
            {errors.actions?.[day]?.actions?.[idx]?.matterId?.message}
          </FormHelperText>
        )}
      </Stack>

      {createMatterOnDecom && (
        <Stack
          sx={{
            bgcolor: 'info.light',
            border: '1px solid',
            borderColor:
              errors.actions?.[day]?.actions?.[idx]
                ?.matterNameTemplateCustomText ||
              errors.actions?.[day]?.actions?.[idx]
                ?.matterNameTemplateProfileField
                ? 'error.main'
                : 'info.main',
            p: 2,
            borderRadius: 1,
          }}
          gap={1}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            color={
              errors.actions?.[day]?.actions?.[idx]
                ?.matterNameTemplateCustomText ||
              errors.actions?.[day]?.actions?.[idx]
                ?.matterNameTemplateProfileField
                ? 'error.main'
                : 'info.dark'
            }
          >
            Auto-Create Matter is enabled for this decommission action.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Matter Name Template:</strong>{' '}
            {matterNameTemplateCustomText} - {matterNameTemplateProfileField}
          </Typography>
          {matterNameTemplateDescription && (
            <Typography variant="body2" color="text.secondary">
              <strong>Description:</strong> {matterNameTemplateDescription}
            </Typography>
          )}

          {errors.actions?.[day]?.actions?.[idx]?.matterNameTemplateCustomText
            ?.message && (
            <FormHelperText error sx={{ fontSize: '0.875rem' }}>
              Custom Text:{' '}
              {
                errors.actions?.[day]?.actions?.[idx]
                  ?.matterNameTemplateCustomText?.message
              }
            </FormHelperText>
          )}
          {errors.actions?.[day]?.actions?.[idx]?.matterNameTemplateProfileField
            ?.message && (
            <FormHelperText error sx={{ fontSize: '0.875rem' }}>
              Profile Field:{' '}
              {
                errors.actions?.[day]?.actions?.[idx]
                  ?.matterNameTemplateProfileField?.message
              }
            </FormHelperText>
          )}
        </Stack>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Stack direction="row" gap={2} alignItems="center" flexGrow={1}>
          <TextField
            size="small"
            placeholder="Search by name or owner"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={createMatterOnDecom}
            sx={{ width: 300, opacity: createMatterOnDecom ? 0.6 : 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
        <Stack direction="row" gap={2} alignItems="center">
          {createMatterOnDecom && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setValue(
                  `actions.${day}.actions.${idx}.createMatterOnDecom`,
                  false,
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  }
                );
                setValue(
                  `actions.${day}.actions.${idx}.matterNameTemplateCustomText`,
                  '',
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  }
                );
                setValue(
                  `actions.${day}.actions.${idx}.matterNameTemplateProfileField`,
                  '',
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  }
                );
                setValue(
                  `actions.${day}.actions.${idx}.matterNameTemplateDescription`,
                  '',
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  }
                );
                setValue(`actions.${day}.actions.${idx}.matterRegion`, 'ANY', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            >
              Reset Auto-Create
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => setIsCreateMatterOpen(true)}
          >
            {createMatterOnDecom
              ? 'Configure Matter Auto-Create'
              : 'Create Matter'}
          </Button>
        </Stack>
      </Stack>

      <VaultMatterTableOld
        matters={matters}
        isLoading={isLoading}
        isError={isError}
        refetch={refetch}
        matterId={matterId}
        day={day}
        idx={idx}
        setValue={setValue}
        setIsCreateMatterOpen={setIsCreateMatterOpen}
        hasUnfilteredData={!!data && data.length > 0}
        hasError={!!errors.actions?.[day]?.actions?.[idx]?.matterId}
        actionType={actionType}
        setActionTypesWithErrors={setActionTypesWithErrors}
        disabled={createMatterOnDecom}
      />

      {isCreateMatterOpen && (
        <CreateVaultMatterDialog
          open={isCreateMatterOpen}
          handleClose={() => setIsCreateMatterOpen(false)}
          setSelectedMatterId={(id: string) => {
            setValue(
              `actions.${day}.actions.${idx}.createMatterOnDecom`,
              false,
              { shouldDirty: true }
            );
            setValue(`actions.${day}.actions.${idx}.matterId`, id, {
              shouldDirty: true,
              shouldValidate: true,
            });
            clearFieldError(actionType, 'matterId', setActionTypesWithErrors);
            setIsCreateMatterOpen(false);
          }}
          isDecom={true}
          initialCreateMatterOnDecom={createMatterOnDecom}
          initialCustomText={matterNameTemplateCustomText}
          initialProfileField={matterNameTemplateProfileField}
          initialDescription={matterNameTemplateDescription}
          initialMatterRegion={matterRegion}
          onSaveDecomTemplate={(values) => {
            setValue(
              `actions.${day}.actions.${idx}.createMatterOnDecom`,
              values.createMatterOnDecom,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
            setValue(
              `actions.${day}.actions.${idx}.matterNameTemplateCustomText`,
              values.matterNameTemplateCustomText,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
            setValue(
              `actions.${day}.actions.${idx}.matterNameTemplateProfileField`,
              values.matterNameTemplateProfileField,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
            setValue(
              `actions.${day}.actions.${idx}.matterNameTemplateDescription`,
              values.matterNameTemplateDescription,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
            setValue(
              `actions.${day}.actions.${idx}.matterRegion`,
              values.matterRegion,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
            setValue(`actions.${day}.actions.${idx}.matterId`, '', {
              shouldDirty: true,
              shouldValidate: true,
            });
            clearFieldError(actionType, 'matterId', setActionTypesWithErrors);
            setIsCreateMatterOpen(false);
          }}
        />
      )}
    </Stack>
  );
}
